/**
 * Pure parts of the tarif's pricing form (P5-b): the text the inputs hold, and the request body. The rules (ascending bands, valid
 * dates, ranges) are the server's — it answers with its own message — so this only turns text into numbers and says what it cannot read.
 */

import type { PujcovnaTarif } from './api';
import type { CenikVzor } from './cenik-vzory';

export interface DelkaRadek { od: string; koef: string }
export interface SezonaRadek { od: string; koef: string; pausalDny: string; pausalCena: string }
export interface CenikValue { marze: string; delky: DelkaRadek[]; sezony: SezonaRadek[] }

export const MAX_DELEK = 4;
export const MAX_SEZON = 8;

/** Nothing typed in the block yet (a vzor can load without asking). */
export const cenikJePrazdny = (v: CenikValue): boolean =>
  v.marze.trim() === '' && v.delky.every((r) => r.od.trim() === '' && r.koef.trim() === '') && v.sezony.every((r) => !r.od.trim() && !r.koef.trim() && !r.pausalDny.trim() && !r.pausalCena.trim());

export const prazdnyCenik = (): CenikValue => ({ marze: '', delky: [], sezony: [] });

const text = (n: number | null | undefined): string => (n == null ? '' : String(n));

export function cenikSeed(t?: Partial<Pick<PujcovnaTarif, 'marze_proc' | 'delky' | 'sezony'>> | CenikVzor | null): CenikValue {
  return {
    marze: text(t?.marze_proc),
    delky: (t?.delky ?? []).map((d) => ({ od: text(d.od), koef: text(d.koef) })),
    sezony: (t?.sezony ?? []).map((s) => ({ od: s.od, koef: text(s.koef), pausalDny: text(s.pausal?.dny), pausalCena: text(s.pausal?.cena) })),
  };
}

export type CenikBody = { marze_proc: number | null; delky: PujcovnaTarif['delky']; sezony: PujcovnaTarif['sezony'] };
export type CenikVysledek = { ok: true; body: CenikBody } | { ok: false; chyba: string };

const prazdne = (...v: string[]) => v.every((x) => x.trim() === '');
/**
 * A number typed with a decimal comma or point; null = empty, NaN = not a (finite) number. `Infinity` / `1e309` are NaN here: JSON would
 * serialise them as `null`, which the server reads as "not priced" and would silently clear a saved markup.
 */
export const cislo = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number(v.trim().replace(',', '.'));
  return Number.isFinite(n) ? n : NaN;
};

export function cenikToBody(v: CenikValue): CenikVysledek {
  const marze = cislo(v.marze);
  if (marze !== null && Number.isNaN(marze)) return { ok: false, chyba: `Marže „${v.marze}“ není číslo.` };

  const delky: CenikBody['delky'] = [];
  for (const [i, r] of v.delky.entries()) {
    if (prazdne(r.od, r.koef)) continue;
    const od = cislo(r.od);
    const koef = cislo(r.koef);
    if (od === null || koef === null || Number.isNaN(od) || Number.isNaN(koef)) return { ok: false, chyba: `Pásmo délky č. ${i + 1}: vyplňte dny i koeficient číslem.` };
    delky.push({ od, koef });
  }

  const sezony: CenikBody['sezony'] = [];
  for (const [i, r] of v.sezony.entries()) {
    if (prazdne(r.od, r.koef, r.pausalDny, r.pausalCena)) continue;
    const koef = cislo(r.koef);
    if (r.od.trim() === '' || koef === null || Number.isNaN(koef)) return { ok: false, chyba: `Sezóna č. ${i + 1}: vyplňte datum (MM-DD) i koeficient číslem.` };
    let pausal: CenikBody['sezony'][number]['pausal'] = null;
    if (!prazdne(r.pausalDny, r.pausalCena)) {
      const dny = cislo(r.pausalDny);
      const cena = cislo(r.pausalCena);
      if (dny === null || cena === null || Number.isNaN(dny) || Number.isNaN(cena)) return { ok: false, chyba: `Sezóna č. ${i + 1}: paušál potřebuje počet dní i cenu.` };
      pausal = { dny, cena };
    }
    sezony.push({ od: r.od.trim(), koef, pausal });
  }
  return { ok: true, body: { marze_proc: marze, delky, sezony } };
}

/** A short line for the tarif list: what is configured, or that nothing is. */
export function cenikShrnuti(t: Pick<PujcovnaTarif, 'marze_proc' | 'delky' | 'sezony'>): string | null {
  if (t.marze_proc == null) return null;
  const casti = [`Marže ${String(t.marze_proc).replace('.', ',')} %`];
  if (t.delky.length > 0) casti.push(`${t.delky.length} ${t.delky.length === 1 ? 'pásmo' : t.delky.length < 5 ? 'pásma' : 'pásem'} délky`);
  if (t.sezony.length > 0) casti.push(`${t.sezony.length} ${t.sezony.length === 1 ? 'sezóna' : t.sezony.length < 5 ? 'sezóny' : 'sezón'}`);
  return casti.join(' · ');
}
